import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useReminderStore } from '@/store/reminderStore';
import { useToast } from '@/components/ui/use-toast';
import type { Reminder, ReminderFormData, LeadPriority } from '@/types/crm';
import { LEAD_PRIORITIES } from '@/types/crm';

interface ReminderFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId?: string;
  dealId?: string;
  reminder?: Reminder | null;
}

export function ReminderFormDialog({ open, onOpenChange, leadId, dealId, reminder }: ReminderFormDialogProps) {
  const { createReminder, updateReminder } = useReminderStore();
  const { toast } = useToast();
  const isEditing = !!reminder;
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState<ReminderFormData>({
    lead_id: leadId,
    deal_id: dealId,
    title: '',
    due_date: '',
    priority: 'medium',
    notes: '',
  });

  useEffect(() => {
    if (reminder) {
      setFormData({
        lead_id: reminder.lead_id,
        deal_id: reminder.deal_id,
        title: reminder.title,
        due_date: reminder.due_date.slice(0, 16),
        priority: reminder.priority,
        notes: reminder.notes || '',
      });
    } else {
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      setFormData({
        lead_id: leadId,
        deal_id: dealId,
        title: '',
        due_date: now.toISOString().slice(0, 16),
        priority: 'medium',
        notes: '',
      });
    }
  }, [reminder, leadId, dealId, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isEditing && reminder) {
        await updateReminder(reminder.id, formData);
        toast({ title: 'Reminder updated successfully' });
      } else {
        await createReminder(formData);
        toast({ title: 'Reminder created successfully' });
      }
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Reminder' : 'Add Reminder'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter reminder title"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date *</Label>
              <Input
                id="due_date"
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value as LeadPriority })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_PRIORITIES.map((priority) => (
                    <SelectItem key={priority} value={priority} className="capitalize">
                      {priority}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
